import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import {
    DataLoaderUpliftHelper,
    IDataLoaderUpliftHelperOptions,
} from 'src/app/services/data-loader/data-loader-uplift.helper';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    generateTableFullPath,
    ICompiledSQLTokens,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { SqlComposer } from 'src/app/utils/query/sql-composer';
import { BIVizDashboardSales } from '../../mopa-sales/sql-maps/biviz-dashboard-sales-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface AbTestsConversionRateDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IGcpProjectId,
        IQueryCacheService,
        IDataLoaderUpliftHelperOptions {
    /**
     * Pass callback to retrieve max day no for the query.
     * If not provided, the default max day no will be used.
     * @param context - query context
     * @returns Must return a string with integer value or 'unlimited'
     */
    maxDayNoGetter?: (context: IQueryContext) => string;
}

export class AbTestsConversionRateDataLoaderFactory extends Factory<
    DataLoader,
    AbTestsConversionRateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AbTestsConversionRateDataLoaderFactory';

    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(options: AbTestsConversionRateDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                console.log(
                    'AbTestsConversionRateDataLoaderFactory',
                    queryContext
                );

                // ignore columns that are not supported in this query
                const ignoredContextColumnIds = [
                    'ACTIVITY_DATE',
                    'ACTIVITY_APP_VERSION',
                ];

                const revenueAgg = BIVizDashboardSales;

                // identify columns not supported in datasource
                const revenueAggDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        revenueAgg.sqlMap.columns
                    );

                const revenueAggDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        revenueAggDsColumns.missing
                    );

                //filter out ignored columns from dsColumns.present
                revenueAggDsColumns.present =
                    revenueAggDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );

                let getDefaultMaxDayNo = (context: IQueryContext) => {
                    return 'unlimited';
                };

                const maxDayNo = options.maxDayNoGetter
                    ? options.maxDayNoGetter(queryContext)
                    : getDefaultMaxDayNo(queryContext);

                const maxDayNoSQL: Partial<ICompiledSQLTokens> | undefined =
                    maxDayNo !== 'unlimited'
                        ? {
                              where: [`day_no <= ${maxDayNo}`],
                          }
                        : undefined;

                const revenueAggSQL =
                    revenueAgg.sqlStatementsCompiler.compileSQLStatements(
                        revenueAggDsColumns.present,
                        queryContext.settings,
                        options,
                        maxDayNoSQL
                    );

                const usersdashActivity = BiVizDashboardUsersActivity;

                // identify columns not supported in datasource
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );

                usersdashActivityDsColumns.present =
                    usersdashActivityDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );

                const usersdashActivityDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        usersdashActivityDsColumns.missing
                    );

                const usersdashActivityCohortSizeSQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options,
                        { where: ['day_no = 0'] }
                    );

                const usersdashActivityActiveUsersSQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                        // { where: ['day_no = 0'] }
                    );

                const usd_pln =
                    queryContext.settings[QueryContextSettingID.CURRENCY] ??
                    'pln';

                // TODO refactor datasource column names

                // if only cohort split group then 'cohort_split_group AS split_group'
                // if only ads flow split group then 'ads_flow_split_group AS split_group'
                // if both then 'CONCAT(cohort_split_group, ', ', ads_flow_split_group) AS split_group'
                // if none then ''

                const windowRunning = `${
                    revenueAggSQL.splitGroupBy != ''
                        ? 'PARTITION BY split_group '
                        : ''
                }ORDER BY day_no`;

                /*
                const windowTotal = `${
                    revenueAggSQL.splitGroupBy != ''
                        ? 'PARTITION BY split_group '
                        : ''
                }`;
                */

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH revenueBase AS
(
  SELECT 
    ds.*,
    ${revenueAggSQL.splitSelect}
    ${revenueAggSQL.datasourceSpecificSplitSelect}
    ${revenueAggSQL.cohortSplitSelect}
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      revenueAgg.sqlMap.dataset,
      revenueAgg.sqlMap.tableId
  )}\` ds
  ${revenueAggSQL.join}
  ${revenueAggSQL.where}
)

, revenue AS
(
  SELECT
    ${revenueAggSQL.splitGroupBy}
    day_no
    , SUM(cnt_first_transaction) OVER (${windowRunning}) AS cumulative_conversions
    , payers
  FROM 
  (
    SELECT
      ${revenueAggSQL.splitGroupBy}
      day_no
      , SUM(cnt_first_transaction) AS cnt_first_transaction
      -- must user DISTINCT because of the granularity of the data
      , COUNT(DISTINCT payer_id) AS payers
    FROM revenueBase
    ${SqlComposer.GROUP_BY([revenueAggSQL.splitGroup, 'day_no'])}
  )
)

, cohort_size AS
(
  SELECT
    ${revenueAggSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    cohort_size
  FROM
  (
    SELECT 
      ${
          usersdashActivityCohortSizeSQL.splitGroupBy != ''
              ? `${usersdashActivityCohortSizeSQL.splitSelect.replace(
                    'AS split_group',
                    'AS cohort_split_group'
                )}`
              : ''
      }
      COUNT(DISTINCT player_id) AS cohort_size
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        usersdashActivity.sqlMap.dataset,
        usersdashActivity.sqlMap.tableId
    )}\` ds
    ${usersdashActivityCohortSizeSQL.join}
    ${usersdashActivityCohortSizeSQL.where}
    ${SqlComposer.GROUP_BY([revenueAggSQL.cohortSplitGroupAlias])}
  )
  ${
      // if split revenue specific columns are selected, then we need to add them to the active users query
      revenueAggSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${revenueAggSQL.datasourceSpecificSplitGroupAlias} FROM revenueBase)`
          : ''
  }
)

-- active users
, active_users AS
(
  SELECT
    ${revenueAggSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    day_no
    , active_users
  FROM
  (
    SELECT 
      ${
          usersdashActivityActiveUsersSQL.splitGroupBy != ''
              ? `${usersdashActivityActiveUsersSQL.splitSelect.replace(
                    'AS split_group',
                    'AS cohort_split_group'
                )}`
              : ''
      }
      day_no
      , COUNT(DISTINCT player_id) AS active_users
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        usersdashActivity.sqlMap.dataset,
        usersdashActivity.sqlMap.tableId
    )}\` ds
    ${usersdashActivityActiveUsersSQL.join}
    ${usersdashActivityActiveUsersSQL.where}
    ${SqlComposer.GROUP_BY([revenueAggSQL.cohortSplitGroupAlias, 'day_no'])}
  )
  ${
      // if split revenue specific columns are selected, then we need to add them to the active users query
      revenueAggSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${revenueAggSQL.datasourceSpecificSplitGroupAlias} FROM revenueBase)`
          : ''
  }
)

SELECT
 ${SqlComposer.Prefix('revenue', revenueAggSQL.splitGroupBy)}
 revenue.day_no
  , cumulative_conversions
  , cohort_size.cohort_size
  , active_users.active_users
  , revenue.payers
  , ROUND(SAFE_DIVIDE(payers, active_users.active_users), 4) AS purchase_rate
  , revenue.cumulative_conversions / cohort_size.cohort_size AS running_cr
FROM revenue
LEFT JOIN cohort_size
  ON ${
      revenueAggSQL.splitGroupBy
          ? 'revenue.split_group = cohort_size.split_group'
          : 'TRUE'
  }
LEFT JOIN active_users
  ON revenue.day_no = active_users.day_no
  AND ${
      revenueAggSQL.splitGroupBy
          ? 'revenue.split_group = active_users.split_group'
          : 'TRUE'
  }
${SqlComposer.ORDER_BY([revenueAggSQL.splitGroup, 'day_no'])}
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                DataLoaderUpliftHelper.processUpliftMetrics(
                    options,
                    queryContext,
                    data
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: revenueAggDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        revenueAggDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}

//  class with metrics documentstion
export class AbTestsConversionRateDataLoaderDocumentation {
    static dataLoaderSourcesDocumentation = /* HTML */ `<h2>Sources</h2>
        <pre>
${generateTableFullPath(
                currentDataProject().gcpProjectId,
                BIVizDashboardSales.sqlMap.dataset,
                BIVizDashboardSales.sqlMap.tableId
            )}</pre
        >
        <pre>
${generateTableFullPath(
                currentDataProject().gcpProjectId,
                BiVizDashboardUsersActivity.sqlMap.dataset,
                BiVizDashboardUsersActivity.sqlMap.tableId
            )}</pre
        >`;

    // metrics documentation
    static metrics = {
        purchase_rate: {
            chartDocumentation: /* HTML */ `<h2>Definition</h2>
                <p>
                    Percentage of players who got any purchase during the day.
                </p>
                <h2>Calculation</h2>
                <p>Number of payers divided by the number of active users.</p>
                ${AbTestsConversionRateDataLoaderDocumentation.dataLoaderSourcesDocumentation}`,
        },
        running_cr: {
            chartDocumentation: /* HTML */ `<h2>Definition</h2>
                <p>
                    Running conversion rate. Shows dynamics of converting into
                    payers. Percentage of players who got it's first purchase
                </p>
                <h2>Calculation</h2>
                <p>
                    Number of conversions divided by the number of players.
                    Number of players is the number of unique players in cohort
                    and is always the same for each day.
                </p>
                ${AbTestsConversionRateDataLoaderDocumentation.dataLoaderSourcesDocumentation}`,
        },
    };
}
